// @ts-nocheck
import React, { useState, useRef, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { motion, AnimatePresence } from 'framer-motion';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { generateCaseReport } from '../services/aiService';

import CaseReportPDF from './CaseReportPDF';
import CaseReportPDFPrestige from './CaseReportPDFPrestige';
import CaseReportPDFElite from './CaseReportPDFElite';
import BrochurePDF from './BrochurePDF';
import StudioPanel from './StudioPanel';
import PlanModal from './PlanModal';
import ClinicalLoupe from './ClinicalLoupe';
import PrestigeModal from './PrestigeModal';
import PatientPortraitViewer from './PatientPortraitViewer';
import VideoPlayer from './VideoPlayer';
import ExtractionViewer from './ExtractionViewer';
import AIDentistPanel from './AIDentistPanel';
import InteractiveComparisonViewer from './InteractiveComparisonViewer';
import { AIDentistAnalysis } from '../types';
import { GenerationResult } from '../App';
import { useClinicSettings } from '../contexts/ClinicSettingsContext';

import PresentationIcon from './icons/PresentationIcon';
import CameraIcon from './icons/CameraIcon';
import Maximize2Icon from './icons/Maximize2Icon';

// jsPDF and html2canvas are now imported as ES modules above

interface ResultDisplayProps {
  generationResults: GenerationResult[];
  activeGenerationIndex: number;
  onActiveGenerationChange: (index: number) => void;
  activeResultIndex: number;
  onActiveResultChange: (index: number) => void;
  onReset: () => void;
  onGoBack: () => void;
  onGenerateVideo: () => void;
  generatedVideoUrl: string | null;
  videoError: string | null;
  onClearVideo: () => void;
  onStartDentureCrop: () => void;
  extractedDentureUrl: string | null;
  extractionError: string | null;
  onClearExtractedDenture: () => void;
  onStartMaskCreation: () => void;
  isExtractionViewerVisible: boolean;
  onCloseExtractionViewer: () => void;
  onOpenExtractionViewer: () => void;
  onGenerateShowcasePortrait: () => void;
  patientPortraitUrl: string | null;
  portraitError: string | null;
  onOpenRefineModal: () => void;
  currentCaseId?: number;
}

const ResultDisplay: React.FC<ResultDisplayProps> = (props) => {
  const { generationResults, activeGenerationIndex, onActiveGenerationChange, activeResultIndex, onActiveResultChange } = props;
  const { settings: clinicSettings } = useClinicSettings();

  const currentGeneration = generationResults[activeGenerationIndex];
  const {
    baseImage, imageUrls: generatedImageUrls, texts: generatedTexts, treatmentPlan,
    smileRating, afterSmileRatings, aiDentistAnalysis, selectedStyles, generationMode, treatmentType
  } = currentGeneration;

  const activeGeneratedUrl = generatedImageUrls[activeResultIndex];
  const activeGeneratedText = generatedTexts?.[activeResultIndex] ?? null;
  const activeAfterSmileRating = afterSmileRatings?.[activeResultIndex] ?? null;

  const [isPlanVisible, setIsPlanVisible] = useState(false);
  const [isLoupeOpen, setIsLoupeOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [isGeneratingPrestigeReport, setIsGeneratingPrestigeReport] = useState(false);
  const [prestigeReportError, setPrestigeReportError] = useState<string | null>(null);
  const [isGeneratingEliteReport, setIsGeneratingEliteReport] = useState(false);
  const [eliteReportError, setEliteReportError] = useState<string | null>(null);
  const [isGeneratingBrochure, setIsGeneratingBrochure] = useState(false);
  const [brochureError, setBrochureError] = useState<string | null>(null);
  const [isPrestigeModalOpen, setIsPrestigeModalOpen] = useState(false);
  const [isPortraitViewerOpen, setIsPortraitViewerOpen] = useState(false);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isVideoViewerOpen, setIsVideoViewerOpen] = useState(false);

  const mainViewerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props.patientPortraitUrl) {
      setIsPortraitViewerOpen(true);
    }
  }, [props.patientPortraitUrl]);

  // Auto-open video viewer when video is generated
  useEffect(() => {
    if (props.generatedVideoUrl) {
      setIsVideoViewerOpen(true);
    }
  }, [props.generatedVideoUrl]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!mainViewerRef.current) return;
    const rect = mainViewerRef.current.getBoundingClientRect();
    mainViewerRef.current.style.setProperty('--mouse-x', `${e.clientX - rect.left}px`);
    mainViewerRef.current.style.setProperty('--mouse-y', `${e.clientY - rect.top}px`);
  };

  const handleImageDownload = () => {
    const link = document.createElement('a');
    link.href = activeGeneratedUrl;
    link.download = `FuturaSmile_After_Vision_${activeResultIndex + 1}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleVideoDownload = () => {
    if (!props.generatedVideoUrl) return;
    const link = document.createElement('a');
    link.href = props.generatedVideoUrl;
    link.download = 'FuturaSmile_Animation.mp4';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGeneratePrestigeReport = useCallback(async () => {
    if (!smileRating || !activeAfterSmileRating) {
      setPrestigeReportError("Complete analysis data is required to generate a report.");
      return;
    }
    setIsGeneratingPrestigeReport(true);
    setPrestigeReportError(null);

    const finalTreatmentPlan = treatmentPlan || {
      summary: {
        treatmentModality: "A comprehensive aesthetic enhancement. The specific clinical pathways to achieve this result will be determined during your personalized consultation."
      }
    };

    try {
      const reportContent = await generateCaseReport(
        treatmentType,
        smileRating,
        activeAfterSmileRating,
        finalTreatmentPlan,
        null
      );
      const originalImageAsDataUrl = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', baseImage.url, true);
        xhr.responseType = 'blob';
        xhr.onload = function () {
          const reader = new FileReader();
          reader.onloadend = function () { resolve(reader.result as string); }
          reader.onerror = reject;
          reader.readAsDataURL(xhr.response);
        };
        xhr.onerror = reject; xhr.send();
      });

      const pdfContainer = document.createElement('div');
      pdfContainer.style.position = 'absolute'; pdfContainer.style.left = '-9999px'; pdfContainer.style.top = '0';
      document.body.appendChild(pdfContainer);

      const root = ReactDOM.createRoot(pdfContainer);
      const renderPromise = new Promise<void>((resolve) => {
        root.render(
          <CaseReportPDFPrestige
            reportData={reportContent} originalImageUrl={originalImageAsDataUrl} generatedImageUrl={activeGeneratedUrl}
            smileRating={smileRating} afterSmileRating={activeAfterSmileRating} patientPortraitUrl={props.patientPortraitUrl}
            clinicSettings={clinicSettings}
            onRendered={resolve}
          />
        );
      });
      await renderPromise;
      await new Promise(resolve => setTimeout(resolve, 2000));

      // jsPDF imported at top of file
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pages = pdfContainer.querySelectorAll('.pdf-page-prestige');
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement;
        const canvas = await html2canvas(page, { scale: 3, useCORS: true, backgroundColor: '#F9F9F7' });
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth(); const pdfHeight = pdf.internal.pageSize.getHeight();
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      }
      pdf.save('FuturaSmile_Prestige_Report.pdf');

      // Save artifact if caseId exists
      if (props.currentCaseId) {
        const pdfBlob = pdf.output('blob');
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        reader.onloadend = () => {
          const base64data = reader.result as string;
          const base64Content = base64data.split(',')[1];
          import('../services/casesApiClient').then(({ saveArtifact }) => {
            saveArtifact(props.currentCaseId!, {
              artifactType: 'pdf_report_prestige',
              content: base64Content,
              metadata: { fileName: 'FuturaSmile_Prestige_Report.pdf', mimeType: 'application/pdf' }
            }).catch(e => console.error("Failed to save prestige report artifact:", e));
          });
        };
      }

      root.unmount(); document.body.removeChild(pdfContainer);
    } catch (err) {
      console.error("PDF Generation Error:", err);
      setPrestigeReportError(err instanceof Error ? err.message : 'An unknown error occurred during report generation.');
    } finally { setIsGeneratingPrestigeReport(false); }
  }, [smileRating, activeAfterSmileRating, treatmentPlan, treatmentType, baseImage.url, activeGeneratedUrl, props.patientPortraitUrl, props.currentCaseId]);


  const handleGenerateReport = useCallback(async () => {
    if (!smileRating || !activeAfterSmileRating || !treatmentPlan) {
      setReportError("Complete analysis data is required to generate a report.");
      return;
    }
    setIsGeneratingReport(true);
    setReportError(null);

    try {
      const reportContent = await generateCaseReport(
        treatmentType, smileRating, activeAfterSmileRating, treatmentPlan, null
      );
      const originalImageAsDataUrl = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', baseImage.url, true); xhr.responseType = 'blob';
        xhr.onload = function () {
          const reader = new FileReader();
          reader.onloadend = function () { resolve(reader.result as string); }
          reader.onerror = reject;
          reader.readAsDataURL(xhr.response);
        };
        xhr.onerror = reject; xhr.send();
      });
      const pdfContainer = document.createElement('div');
      pdfContainer.style.position = 'absolute'; pdfContainer.style.left = '-9999px'; pdfContainer.style.top = '0';
      document.body.appendChild(pdfContainer);

      const root = ReactDOM.createRoot(pdfContainer);
      const renderPromise = new Promise<void>((resolve) => {
        root.render(
          <CaseReportPDF
            reportData={reportContent} originalImageUrl={originalImageAsDataUrl} generatedImageUrl={activeGeneratedUrl}
            smileRating={smileRating} afterSmileRating={activeAfterSmileRating} onRendered={resolve}
          />
        );
      });
      await renderPromise;
      await new Promise(resolve => setTimeout(resolve, 2000));

      // jsPDF imported at top of file
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pages = pdfContainer.querySelectorAll('.pdf-page');
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement;
        const canvas = await html2canvas(page, { scale: 3, useCORS: true, backgroundColor: null });
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth(); const pdfHeight = pdf.internal.pageSize.getHeight();
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      }
      pdf.save('FuturaSmile_Case_Report.pdf');

      // Save artifact if caseId exists
      if (props.currentCaseId) {
        const pdfBlob = pdf.output('blob');
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        reader.onloadend = () => {
          const base64data = reader.result as string;
          const base64Content = base64data.split(',')[1];
          import('../services/casesApiClient').then(({ saveArtifact }) => {
            saveArtifact(props.currentCaseId!, {
              artifactType: 'pdf_report',
              content: base64Content,
              metadata: { fileName: 'FuturaSmile_Case_Report.pdf', mimeType: 'application/pdf' }
            }).catch(e => console.error("Failed to save report artifact:", e));
          });
        };
      }

      root.unmount(); document.body.removeChild(pdfContainer);
    } catch (err) {
      console.error("PDF Generation Error:", err);
      setReportError(err instanceof Error ? err.message : 'An unknown error occurred during report generation.');
    } finally { setIsGeneratingReport(false); }
  }, [smileRating, activeAfterSmileRating, treatmentPlan, treatmentType, baseImage.url, activeGeneratedUrl, props.currentCaseId]);

  const handleGenerateEliteReport = useCallback(async () => {
    if (!smileRating || !activeAfterSmileRating) {
      setEliteReportError("Complete analysis data is required to generate a report.");
      return;
    }
    setIsGeneratingEliteReport(true);
    setEliteReportError("Generating specialized studio cover...");

    const finalTreatmentPlan = treatmentPlan || {
      summary: {
        treatmentModality: "A comprehensive aesthetic enhancement. The specific clinical pathways to achieve this result will be determined during your personalized consultation."
      }
    };

    try {
      // 1. Fetch the Active Generated Image (After) as Base64 for Cover Generation
      const generatedImageBase64 = await new Promise<{ base64: string, mimeType: string }>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', activeGeneratedUrl, true);
        xhr.responseType = 'blob';
        xhr.onload = function () {
          const reader = new FileReader();
          reader.onloadend = function () {
            const base64data = reader.result as string;
            resolve({
              base64: base64data.split(',')[1],
              mimeType: xhr.response.type
            });
          }
          reader.onerror = reject;
          reader.readAsDataURL(xhr.response);
        };
        xhr.onerror = reject;
        xhr.send();
      });

      // 2. Call the Backend API to Generate the 16:9 Studio Cover
      let coverImageUrl = activeGeneratedUrl;
      try {
        const coverResponse = await import('../services/aiService').then(m =>
          m.generateEliteCover(
            generatedImageBase64.base64,
            generatedImageBase64.mimeType,
            props.currentCaseId,
            '16:9',
            clinicSettings?.accentColor || '#14b8a6'
          )
        );
        if (coverResponse && coverResponse.imageUrl) {
          coverImageUrl = coverResponse.imageUrl;
        }
      } catch (coverErr) {
        console.warn("Failed to generate elite cover, falling back to standard image:", coverErr);
        // Continue with standard image if cover generation fails, but log it.
      }

      setEliteReportError("Compiling PDF report...");

      // 3. Generate Report Content
      const reportContent = await generateCaseReport(
        treatmentType,
        smileRating,
        activeAfterSmileRating,
        finalTreatmentPlan,
        null
      );

      // 4. Fetch Original Image as Data URL
      const originalImageAsDataUrl = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', baseImage.url, true);
        xhr.responseType = 'blob';
        xhr.onload = function () {
          const reader = new FileReader();
          reader.onloadend = function () { resolve(reader.result as string); }
          reader.onerror = reject;
          reader.readAsDataURL(xhr.response);
        };
        xhr.onerror = reject; xhr.send();
      });

      const pdfContainer = document.createElement('div');
      pdfContainer.style.position = 'absolute'; pdfContainer.style.left = '-9999px'; pdfContainer.style.top = '0';
      document.body.appendChild(pdfContainer);

      const root = ReactDOM.createRoot(pdfContainer);
      const renderPromise = new Promise<void>((resolve) => {
        root.render(
          <CaseReportPDFElite
            reportData={reportContent}
            originalImageUrl={originalImageAsDataUrl}
            generatedImageUrl={activeGeneratedUrl}
            coverImageUrl={coverImageUrl} // Pass the generated cover
            smileRating={smileRating}
            afterSmileRating={activeAfterSmileRating}
            patientPortraitUrl={props.patientPortraitUrl}
            treatmentType={treatmentType}
            clinicSettings={clinicSettings}
            onRendered={resolve}
          />
        );
      });
      await renderPromise;
      await new Promise(resolve => setTimeout(resolve, 2500)); // Slightly longer for more pages

      // jsPDF imported at top of file
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' }); // Landscape
      const pages = pdfContainer.querySelectorAll('.pdf-page-elite');

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement;
        const canvas = await html2canvas(page, { scale: 3, useCORS: true, backgroundColor: '#FAFAF9' });
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth(); const pdfHeight = pdf.internal.pageSize.getHeight();
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      }
      pdf.save('FuturaSmile_Elite_Report.pdf');

      // Save artifact if caseId exists
      if (props.currentCaseId) {
        const pdfBlob = pdf.output('blob');
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        reader.onloadend = () => {
          const base64data = reader.result as string;
          const base64Content = base64data.split(',')[1];
          import('../services/casesApiClient').then(({ saveArtifact }) => {
            saveArtifact(props.currentCaseId!, {
              artifactType: 'pdf_report_elite',
              content: base64Content,
              metadata: { fileName: 'FuturaSmile_Elite_Report.pdf', mimeType: 'application/pdf' }
            }).catch(e => console.error("Failed to save elite report artifact:", e));
          });
        };
      }

      root.unmount(); document.body.removeChild(pdfContainer);
      setEliteReportError(null);
    } catch (err) {
      console.error("Elite PDF Generation Error:", err);
      setEliteReportError(err instanceof Error ? err.message : 'An unknown error occurred during report generation.');
    } finally { setIsGeneratingEliteReport(false); }
  }, [smileRating, activeAfterSmileRating, treatmentPlan, treatmentType, baseImage.url, activeGeneratedUrl, props.patientPortraitUrl, props.currentCaseId, clinicSettings]);

  const handleGenerateBrochure = useCallback(async () => {
    if (!smileRating || !activeAfterSmileRating) {
      setBrochureError("Complete analysis data is required to generate a brochure.");
      return;
    }
    setIsGeneratingBrochure(true);
    setBrochureError(null);

    const finalTreatmentPlan = treatmentPlan || {
      summary: {
        treatmentModality: "A comprehensive aesthetic enhancement."
      }
    };

    try {
      // Generate Report Content
      const reportContent = await generateCaseReport(
        treatmentType,
        smileRating,
        activeAfterSmileRating,
        finalTreatmentPlan,
        null
      );

      // Fetch Original Image as Data URL
      const originalImageAsDataUrl = await new Promise<string>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', baseImage.url, true);
        xhr.responseType = 'blob';
        xhr.onload = function () {
          const reader = new FileReader();
          reader.onloadend = function () { resolve(reader.result as string); }
          reader.onerror = reject;
          reader.readAsDataURL(xhr.response);
        };
        xhr.onerror = reject; xhr.send();
      });

      const pdfContainer = document.createElement('div');
      pdfContainer.style.position = 'absolute'; pdfContainer.style.left = '-9999px'; pdfContainer.style.top = '0';
      document.body.appendChild(pdfContainer);

      const root = ReactDOM.createRoot(pdfContainer);
      const renderPromise = new Promise<void>((resolve) => {
        root.render(
          <BrochurePDF
            reportData={reportContent}
            originalImageUrl={originalImageAsDataUrl}
            generatedImageUrl={activeGeneratedUrl}
            smileRating={smileRating}
            afterSmileRating={activeAfterSmileRating}
            treatmentType={treatmentType}
            clinicSettings={clinicSettings}
            patientName={reportContent?.patientName}
            onRendered={resolve}
          />
        );
      });
      await renderPromise;
      await new Promise(resolve => setTimeout(resolve, 1500));

      // jsPDF imported at top of file
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pages = pdfContainer.querySelectorAll('.pdf-page-brochure');

      for (let i = 0; i < pages.length; i++) {
        const page = pages[i] as HTMLElement;
        const canvas = await html2canvas(page, { scale: 3, useCORS: true, backgroundColor: '#FFFFFF' });
        const imgData = canvas.toDataURL('image/png');
        const pdfWidth = pdf.internal.pageSize.getWidth(); const pdfHeight = pdf.internal.pageSize.getHeight();
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight, undefined, 'FAST');
      }
      pdf.save('FuturaSmile_Brochure.pdf');

      // Save artifact if caseId exists
      if (props.currentCaseId) {
        const pdfBlob = pdf.output('blob');
        const reader = new FileReader();
        reader.readAsDataURL(pdfBlob);
        reader.onloadend = () => {
          const base64data = reader.result as string;
          const base64Content = base64data.split(',')[1];
          import('../services/casesApiClient').then(({ saveArtifact }) => {
            saveArtifact(props.currentCaseId!, {
              artifactType: 'pdf_brochure',
              content: base64Content,
              metadata: { fileName: 'FuturaSmile_Brochure.pdf' }
            }).catch(e => console.error("Failed to save brochure artifact:", e));
          });
        };
      }

      root.unmount(); document.body.removeChild(pdfContainer);
    } catch (err) {
      console.error("Brochure PDF Generation Error:", err);
      setBrochureError(err instanceof Error ? err.message : 'An unknown error occurred during brochure generation.');
    } finally { setIsGeneratingBrochure(false); }
  }, [smileRating, activeAfterSmileRating, treatmentPlan, treatmentType, baseImage.url, activeGeneratedUrl, props.currentCaseId, clinicSettings]);

  return (
    <div className="w-full h-full bg-zinc-950 text-zinc-200 flex overflow-hidden">
      <AnimatePresence>
        {aiDentistAnalysis && <AIDentistPanel analysis={aiDentistAnalysis} />}
      </AnimatePresence>

      <main ref={mainViewerRef} onMouseMove={handleMouseMove} className="flex-1 flex items-center justify-center p-8 relative min-w-0 grid-bg mouse-spotlight">
        <AnimatePresence>
          {!isPresentationMode && (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="absolute top-6 right-6 z-30 flex items-center gap-2"
            >
              {props.generatedVideoUrl && !isVideoViewerOpen && (
                <button onClick={() => setIsVideoViewerOpen(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-teal-600 to-teal-500 rounded-lg border border-teal-400/30 hover:from-teal-500 hover:to-teal-400 transition-all shadow-lg shadow-teal-500/20" title="View Generated Video">
                  <span className="material-symbols-outlined text-[18px]">play_circle</span> View Video
                </button>
              )}
              {props.patientPortraitUrl && (
                <button onClick={() => setIsPortraitViewerOpen(true)} className="flex items-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-zinc-800/50 backdrop-blur-md rounded-lg border border-white/20 hover:bg-zinc-700/70 transition-colors shadow-lg" title="View Portrait Photo">
                  <CameraIcon className="w-4 h-4" /> View Portrait Photo
                </button>
              )}
              <button onClick={() => setIsPresentationMode(true)} className="p-2 text-sm font-semibold text-white bg-zinc-800/50 backdrop-blur-md rounded-lg border border-white/20 hover:bg-zinc-700/70 transition-colors shadow-lg" title="Presentation Mode">
                <PresentationIcon className="w-5 h-5" />
              </button>
              <button onClick={() => setIsLoupeOpen(true)} className="p-2 text-sm font-semibold text-white bg-zinc-800/50 backdrop-blur-md rounded-lg border border-white/20 hover:bg-zinc-700/70 transition-colors shadow-lg" title="Fullscreen Loupe">
                <Maximize2Icon className="w-5 h-5" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
        {generationResults.length > 1 && !isPresentationMode && (
          <motion.div
            initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.4 } }} exit={{ opacity: 0, y: -20 }}
            className="absolute top-6 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 p-1.5 rounded-full bg-zinc-950/60 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50"
          >
            {generationResults.map((_, index) => (
              <button key={index} onClick={() => onActiveGenerationChange(index)} className={`relative px-4 py-2 text-sm font-semibold rounded-full transition-colors ${activeGenerationIndex === index ? 'text-white' : 'text-zinc-400 hover:text-white'}`}>
                {activeGenerationIndex === index && <motion.div layoutId="smile-set-switcher" className="absolute inset-0 bg-white/10 rounded-full" transition={{ type: 'spring', stiffness: 300, damping: 25 }} />}
                <span className="relative z-10">Design {index + 1}</span>
              </button>
            ))}
          </motion.div>
        )}

        <AnimatePresence>
          {!smileRating && !isPresentationMode && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-20 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-4 py-2 rounded-full bg-teal-950/80 backdrop-blur-md border border-teal-500/30 shadow-lg"
            >
              <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-medium text-teal-200">Analyzing...</span>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait">
          <motion.div
            key={isVideoViewerOpen && props.generatedVideoUrl ? 'video' : `main-viewer-${activeGenerationIndex}-${activeResultIndex}`}
            initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }} className="w-full h-full flex items-center justify-center">

            {isVideoViewerOpen && props.generatedVideoUrl ? (
              <div className="relative w-full h-full max-w-7xl max-h-[calc(100vh-200px)] rounded-2xl overflow-hidden shadow-2xl shadow-black/30 flex items-center justify-center bg-black">
                <VideoPlayer src={props.generatedVideoUrl} poster={activeGeneratedUrl} onClose={() => setIsVideoViewerOpen(false)} />
              </div>
            ) : (
              <div className="w-full h-full max-w-[90%] max-h-[85vh] rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/5">
                <InteractiveComparisonViewer
                  beforeImageUrl={baseImage.url}
                  afterImageUrl={activeGeneratedUrl}
                />
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <AnimatePresence>
          {!props.generatedVideoUrl && !isPresentationMode && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0, transition: { delay: 0.5, duration: 0.4 } }} exit={{ opacity: 0, y: 20 }} className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-4">
              {generatedImageUrls.length > 1 && (
                <div className="flex items-center gap-2 p-1.5 rounded-full bg-zinc-950/60 backdrop-blur-xl border border-white/10 shadow-2xl shadow-black/50">
                  {generatedImageUrls.map((_, index) => {
                    const isActive = activeResultIndex === index;
                    const label = `Result ${index + 1}`;

                    return (
                      <button key={index} onClick={() => onActiveResultChange(index)} className={`relative px-4 py-2 text-sm font-semibold rounded-full transition-colors ${isActive ? 'text-white' : 'text-zinc-400 hover:text-white'}`}>
                        {isActive && <motion.div layoutId="result-switcher" className="absolute inset-0 bg-white/10 rounded-full" transition={{ type: 'spring', stiffness: 300, damping: 25 }} />}
                        <span className="relative z-10 truncate max-w-[120px]">{label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        {isPresentationMode && (
          <button onClick={() => setIsPresentationMode(false)} className="absolute bottom-6 right-6 z-30 p-2 text-sm font-semibold text-white bg-zinc-800/50 backdrop-blur-md rounded-lg border border-white/20 hover:bg-zinc-700/70 transition-colors shadow-lg" title="Exit Presentation Mode">
            <PresentationIcon className="w-5 h-5" />
          </button>
        )}
      </main>

      <AnimatePresence>
        {!isPresentationMode && (
          <motion.div
            initial={{ x: '100%' }} animate={{ x: '0%' }} exit={{ x: '100%' }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="h-full"
          >
            <StudioPanel
              onGoBack={props.onGoBack} onReset={props.onReset} onGenerateVideo={props.onGenerateVideo}
              generatedVideoUrl={props.generatedVideoUrl} videoError={props.videoError} onClearVideo={props.onClearVideo}
              treatmentPlan={treatmentPlan} generatedText={activeGeneratedText} smileRating={smileRating} afterSmileRating={activeAfterSmileRating}
              onGenerateShowcasePortrait={props.onGenerateShowcasePortrait}
              portraitError={props.portraitError} treatmentType={treatmentType} onViewPlan={() => setIsPlanVisible(true)}
              onShowcaseResult={() => setIsPrestigeModalOpen(true)} onGenerateReport={handleGenerateReport} isGeneratingReport={isGeneratingReport}
              reportError={reportError} onGeneratePrestigeReport={handleGeneratePrestigeReport} isGeneratingPrestigeReport={isGeneratingPrestigeReport}
              prestigeReportError={prestigeReportError}
              onDownloadImage={handleImageDownload} onDownloadVideo={handleVideoDownload}

              onStartDentureCrop={props.onStartDentureCrop} extractedDentureUrl={props.extractedDentureUrl} extractionError={props.extractionError}
              onClearExtractedDenture={props.onClearExtractedDenture} onStartMaskCreation={props.onStartMaskCreation}
              onOpenExtractionViewer={props.onOpenExtractionViewer}
              onGenerateEliteReport={handleGenerateEliteReport} isGeneratingEliteReport={isGeneratingEliteReport}
              eliteReportError={eliteReportError}
              onGenerateBrochure={handleGenerateBrochure} isGeneratingBrochure={isGeneratingBrochure}
              brochureError={brochureError}
              onOpenRefineModal={props.onOpenRefineModal}
            />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isPlanVisible && (<PlanModal plan={treatmentPlan} genText={activeGeneratedText} onClose={() => setIsPlanVisible(false)} />)}
      </AnimatePresence>
      <AnimatePresence>
        {isLoupeOpen && (<ClinicalLoupe onClose={() => setIsLoupeOpen(false)} originalImageUrl={baseImage.url} generatedImageUrl={activeGeneratedUrl} initialAspectRatio={null} />)}
      </AnimatePresence>
      <AnimatePresence>
        {isPrestigeModalOpen && smileRating && activeAfterSmileRating && (
          <PrestigeModal isOpen={isPrestigeModalOpen} onClose={() => setIsPrestigeModalOpen(false)} originalImageUrl={baseImage.url} generatedImageUrl={activeGeneratedUrl} smileRating={smileRating} afterSmileRating={activeAfterSmileRating} />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {props.isExtractionViewerVisible && props.extractedDentureUrl && (<ExtractionViewer imageUrl={props.extractedDentureUrl} onClose={props.onCloseExtractionViewer} />)}
      </AnimatePresence>
      <AnimatePresence>
        {isPortraitViewerOpen && props.patientPortraitUrl && (<PatientPortraitViewer imageUrl={props.patientPortraitUrl} onClose={() => setIsPortraitViewerOpen(false)} />)}
      </AnimatePresence>
    </div>
  );
};

export default ResultDisplay;